/**
 * Static configuration for the Annotation Productivity page.
 *
 * The two kinds of work measure different things, so each tab owns its columns
 * and its own KPI band rather than sharing one wide table.
 */

/**
 * TEMPORARY MIRROR of a backend rule — replace as soon as the backend exposes an
 * authoritative signal.
 *
 * The endpoint is scoped to staff by email domain, which is narrower than
 * console2's INTERNAL permission, so a client-side INTERNAL user would otherwise
 * reach the page and have every request rejected. The backend compares the
 * segment after the last "@" of the DB `User.email`, trimmed and lowercased,
 * against "udyogyantra.ai" — subdomains rejected. The suffix test below is
 * equivalent, including the trim.
 *
 * Why this must not stay: the rule is a hardcoded constant on the backend, it is
 * scheduled to be replaced by an immutable internal signal, and the email itself
 * is forgeable. When the backend publishes a boolean (login payload or a
 * per-module canView map), consume that and delete this.
 *
 * One divergence to be aware of: the backend reads `User.email` from the
 * database, this reads the login payload. They agree today.
 */
const STAFF_EMAIL_DOMAIN = '@udyogyantra.ai';

export const isAnnotationDashboardUser = (userConfig) => {
	const email = userConfig?.userDetails?.email ?? '';
	return email.trim().toLowerCase().endsWith(STAFF_EMAIL_DOMAIN);
};

/**
 * `statusCode: 300` is this codebase's generic application-error code, so it
 * cannot be used on its own to recognise a refusal. The message prefix is stable;
 * the " (ref - <n>)" the backend appends is not, so match only the prefix.
 *
 * This is a stopgap for the same reason as the mirror above — an explicit signal
 * from the backend would remove the need to read error prose.
 */
const ACCESS_DENIED_PREFIX = 'this dashboard is internal-only';

export const isAccessDeniedError = (error) => {
	const message = typeof error === 'string' ? error : error?.message ?? '';
	return message.trim().toLowerCase().startsWith(ACCESS_DENIED_PREFIX);
};

/**
 * ImagesFinder defaults to today->today. For a D-1 dashboard that range never
 * has built data, so the page would open empty on every visit with no way to
 * tell that apart from "nobody annotated anything". Default to the last full
 * calendar month, which is always populated.
 *
 * Frozen at module scope on purpose: ImagesFinder runs
 * useEffect(..., [defaultState]), so a fresh object each render would
 * re-dispatch the default and stamp on the user's own selection.
 */
const buildDefaultRange = () => {
	const now = new Date();
	return {
		active: true,
		from: new Date(now.getFullYear(), now.getMonth() - 1, 1),
		to: new Date(now.getFullYear(), now.getMonth(), 0),
	};
};

export const FINDER_DEFAULT_STATE = {
	DATE_RANGE: buildDefaultRange(),
};

export const TAB_CONSOLE = 'console';
export const TAB_CVAT = 'cvat';

export const TABS = [
	{
		key: TAB_CONSOLE,
		label: 'Console Annotation',
	},
	{
		key: TAB_CVAT,
		label: 'CVAT Annotation',
	},
];

const BASE_COLUMNS = [
	{
		key: 'serial',
		title: 'S.No',
		fixed: true,
		align: 'center',
		width: 80,
	},
	{
		key: 'user',
		title: 'Annotator',
		sortable: true,
		align: 'left',
		width: 180,
	},
	{
		key: 'customer',
		title: 'Customer',
		sortable: true,
		align: 'left',
		width: 220,
	},
];

export const CONSOLE_COLUMNS = [
	...BASE_COLUMNS,
	{
		key: 'productIdsAnnotated',
		title: 'Product IDs Annotated',
		sortable: true,
		align: 'center',
		width: 210,
	},
	{
		key: 'productIdsReviewed',
		title: 'Product IDs Reviewed',
		sortable: true,
		align: 'center',
		width: 205,
	},
];

export const CVAT_COLUMNS = [
	...BASE_COLUMNS,
	{
		key: 'imagesAllocated',
		title: 'Images Allocated',
		sortable: true,
		align: 'center',
		width: 175,
	},
	{
		key: 'imagesAnnotated',
		title: 'Images Annotated',
		sortable: true,
		align: 'center',
		width: 180,
	},
	{
		key: 'percentCompleted',
		title: '% Completed',
		sortable: true,
		align: 'center',
		width: 155,
	},
	{
		key: 'shapesDrawn',
		title: 'Shapes Drawn',
		sortable: true,
		align: 'center',
		width: 165,
	},
];

/**
 * VariantTable sorts on the mapped field, so `percentCompleted` maps to the
 * numeric `percentSort` rather than the nullable display value.
 */
export const COLUMN_MAPPING = {
	user: 'user',
	customer: 'customer',
	imagesAllocated: 'imagesAllocated',
	imagesAnnotated: 'imagesAnnotated',
	percentCompleted: 'percentSort',
	shapesDrawn: 'shapesDrawn',
	productIdsAnnotated: 'productIdsAnnotated',
	productIdsReviewed: 'productIdsReviewed',
};

/**
 * Cards for the shared StatsComp (src/Pages/ProductionAnalysis/StatsComp.js).
 * One colour across the band on purpose: every card is the same kind of measure,
 * so varying the hue would imply a distinction that does not exist.
 */
const KPI_COLOUR = '#4a70fb';

const toCard = (label, value) => {
	return {
		label,
		value,
		color: KPI_COLOUR,
	};
};

export const getKpiCards = (tab, totals, formatValue) => {
	if (tab === TAB_CVAT) {
		return [
			toCard('Images Allocated', formatValue(totals.imagesAllocated)),
			toCard('Images Annotated', formatValue(totals.imagesAnnotated)),
			toCard('Shapes Drawn', formatValue(totals.shapesDrawn)),
		];
	}
	return [
		toCard('Product IDs Annotated', formatValue(totals.productIdsAnnotated)),
		toCard('Product IDs Reviewed', formatValue(totals.productIdsReviewed)),
	];
};

export const getColumnsForTab = (tab) => {
	return tab === TAB_CVAT ? CVAT_COLUMNS : CONSOLE_COLUMNS;
};

export const getEmptyMessageForTab = (tab) => {
	return tab === TAB_CVAT
		? 'No CVAT annotation completed in this range'
		: 'No console annotation completed in this range';
};
